import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft } from 'lucide-react';
import { ShopNavigation } from '../components/shop/ShopNavigation';
import { ShopFooter } from '../components/shop/ShopFooter';
import { AddToCart } from '../components/shop/AddToCart';
import { QuantitySelector } from '../components/shop/QuantitySelector';
import { ShippingMessage } from '../components/shop/ShippingMessage';
import { RestockNotify } from '../components/shop/RestockNotify';
import { formatMoney, getProduct, pricePerCan, shipping } from '../lib/shopify';
import type { Product as ProductType } from '../types/commerce';
const EASE = [0.23, 1, 0.32, 1] as const;
export function Product() {
  const {
    handle = ''
  } = useParams();
  const [product, setProduct] = useState<ProductType | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeImage, setActiveImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  useEffect(() => {
    let active = true;
    setLoading(true);
    setActiveImage(0);
    setQuantity(1);
    window.scrollTo({
      top: 0
    });
    getProduct(handle).then(result => {
      if (!active) return;
      setProduct(result);
      if (result) document.title = `${result.name} ${result.packLabel} — BEAT`;
    }).catch(() => {
      if (active) setProduct(null);
    }).finally(() => {
      if (active) setLoading(false);
    });
    return () => {
      active = false;
    };
  }, [handle]);
  return <div className="flex min-h-screen w-full flex-col bg-beat-black font-sans text-white antialiased" data-id="element-334" data-mp="3vp3akxk1wujy">
      <ShopNavigation data-id="element-335" data-mp="3qq055c05smbg" />

      <main className="flex-1 px-5 py-10 md:px-10 md:py-16" data-id="element-336" data-mp="3lp2myzf5fs9y">
        <div className="mx-auto max-w-[1200px]" data-id="element-337" data-mp="3lqngqmtn376r">
          <Link to="/shop" className="inline-flex items-center gap-2 text-sm text-white/50 transition-colors duration-150 hover:text-white" data-id="element-338" data-mp="1ox2g7r9i6dvs">
            <ArrowLeft size={16} data-id="element-339" data-mp="3edo9zwt4aigr" /> All packs
          </Link>

          {loading ? <div className="mt-8 grid animate-pulse gap-10 lg:grid-cols-2" data-id="element-340" data-mp="3ebv1ebyy1o64">
              <div className="aspect-[4/5] rounded-3xl bg-white/5" data-id="element-341" data-mp="3j5zcn7f8sid0" />
              <div className="space-y-4" data-id="element-342" data-mp="34g9wo62hq9so">
                <div className="h-10 w-2/3 rounded-full bg-white/5" data-id="element-343" data-mp="34dfaoa3twgx5" />
                <div className="h-5 w-full rounded-full bg-white/5" data-id="element-344" data-mp="39fd23rmdui16" />
                <div className="h-5 w-4/5 rounded-full bg-white/5" data-id="element-345" data-mp="y9w3bz9l6p3" />
              </div>
            </div> : !product ? <div className="py-24 text-center" data-id="element-346" data-mp="5wjmzadyhl4w">
              <h1 className="text-3xl font-medium tracking-tight" data-id="element-347" data-mp="3o3higoxyqhgs">
                We could not find that pack.
              </h1>
              <Link to="/shop" className="mt-8 inline-flex min-h-[52px] items-center rounded-full bg-beat-red px-7 text-base font-medium text-white transition-colors duration-150 hover:bg-white hover:text-beat-black" data-id="element-348" data-mp="3s41hxz60dx63">
                Back to the shop
              </Link>
            </div> : <>
              <div className="mt-8 grid gap-10 lg:grid-cols-2 lg:gap-16" data-id="element-349" data-mp="247k7qfrgqoot">
                {/* Photography */}
                <motion.div initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              duration: 0.5,
              ease: EASE
            }} data-id="element-350" data-mp="2j548tbl8nzpp">
                  <div className="overflow-hidden rounded-3xl bg-white/5" data-id="element-351" data-mp="1qfnh6g5g0muj">
                    <img src={product.images[activeImage]} alt={`${product.name} ${product.packLabel}`} className="aspect-[4/5] w-full object-cover" data-id="element-352" data-mp="1qdkhf4ehnfcm" />
                  </div>
                  {product.images.length > 1 && <div className="mt-4 flex gap-3" data-id="element-353" data-mp="17pfjidkiiolq">
                      {product.images.map((image, index) => <button key={image} type="button" onClick={() => setActiveImage(index)} aria-label={`Show image ${index + 1}`} aria-current={index === activeImage} className={`h-20 w-16 overflow-hidden rounded-xl border transition-colors duration-150 ${index === activeImage ? 'border-beat-red' : 'border-white/10 hover:border-white/40'}`} data-id="element-354" data-mp="12qukghgzyayv" data-mpi={index}>
                          <img src={image} alt="" className="h-full w-full object-cover" data-id="element-355" data-mp="tmp6ct460h0f" />
                        </button>)}
                    </div>}
                </motion.div>

                {/* Buy box */}
                <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.6,
              ease: EASE
            }} className="lg:pt-6" data-id="element-356" data-mp="34fs1ikoa3hso">
                  <p className="mb-5 text-[11px] font-medium uppercase tracking-[0.2em] text-beat-red sm:text-sm sm:tracking-widest" data-id="element-357" data-mp="2s494ji24qv4i">
                    {product.packLabel} · {product.cans} cans
                  </p>
                  <h1 className="text-[11vw] font-medium leading-[1.05] tracking-tight sm:text-5xl md:text-6xl" data-id="element-358" data-mp="3fvwm39di4tu4">
                    {product.name}
                  </h1>
                  <p className="mt-6 max-w-md text-lg leading-relaxed text-white/60" data-id="element-359" data-mp="3axmpcuc4pl6e">
                    {product.description}
                  </p>

                  <div className="mt-8 flex items-baseline gap-3" data-id="element-360" data-mp="31wom4e9ilhov">
                    <span className="text-3xl font-medium tabular-nums" data-id="element-361" data-mp="31yhr7p4hbtur">
                      {formatMoney(product.price.amount, product.price.currencyCode)}
                    </span>
                    {product.compareAtPrice && <span className="text-lg text-white/35 line-through tabular-nums" data-id="element-362" data-mp="1wx8eya4ypz8w">
                        {formatMoney(product.compareAtPrice.amount, product.compareAtPrice.currencyCode)}
                      </span>}
                    <span className="text-sm text-white/40" data-id="element-363" data-mp="197e581fxwqsv">
                      {pricePerCan(product)} per can
                    </span>
                  </div>

                  {product.availableForSale ? <>
                      <div className="mt-8 flex flex-wrap items-center gap-4" data-id="element-364" data-mp="zbb5t3ch369m">
                        <QuantitySelector value={quantity} onChange={setQuantity} label={`Quantity of ${product.name} ${product.packLabel}`} data-id="element-365" data-mp="149cp9pdt97lm" />
                        <AddToCart product={product} quantity={quantity} className="flex-1 sm:flex-none sm:min-w-[200px]" data-id="element-366" data-mp="bm36kq9qewl7" />
                      </div>
                      <ShippingMessage className="mt-6" data-id="element-367" data-mp="knkhzvc72a3k" />
                    </> : <div className="mt-8 rounded-3xl border border-white/10 p-6" data-id="element-368" data-mp="fmwqz9nytt9c">
                      <p className="text-sm font-medium uppercase tracking-widest text-beat-red" data-id="element-369" data-mp="ujxc2ubnipyu">
                        {product.badge ?? 'Sold out'}
                      </p>
                      <RestockNotify product={product} layout="panel" className="mt-4" data-id="element-370" data-mp="13dsis6sf8ogl" />
                    </div>}
                </motion.div>
              </div>

              {/* Product truth */}
              <section className="mt-20 grid gap-10 border-t border-white/10 pt-14 md:mt-28 md:grid-cols-3 md:gap-14" data-id="element-371" data-mp="18d558qfdrtin">
                <div data-id="element-372" data-mp="2fo1bh84avo1u">
                  <h2 className="text-sm font-medium uppercase tracking-widest text-beat-red" data-id="element-373" data-mp="2fojc0gxzwofk">
                    Ingredients
                  </h2>
                  <ul className="mt-5 space-y-2 text-base text-white/60" data-id="element-374" data-mp="2op7ubnuf9ay9">
                    {product.ingredients.map(ingredient => <li key={ingredient} data-id="element-375" data-mp="2tnsw3rpbo2ot">{ingredient}</li>)}
                  </ul>
                </div>

                <div data-id="element-376" data-mp="1vyzbruvz4oj5">
                  <h2 className="text-sm font-medium uppercase tracking-widest text-beat-red" data-id="element-377" data-mp="20zdhv7e0loej">
                    Per 100 ml
                  </h2>
                  <dl className="mt-5 space-y-2 text-base" data-id="element-378" data-mp="25xorpi4b7ywr">
                    {product.nutrition.map(row => <div key={row.label} className="flex justify-between gap-4 border-b border-white/5 pb-2" data-id="element-379" data-mp="2arj92ewg01so">
                        <dt className="text-white/60" data-id="element-380" data-mp="1i7kr7jmf4f9z">{row.label}</dt>
                        <dd className="tabular-nums text-white/80" data-id="element-381" data-mp="1aouzd5znh2yf">
                          {row.value}
                        </dd>
                      </div>)}
                  </dl>
                </div>

                <div data-id="element-382" data-mp="10pvp5iz8kz9b">
                  <h2 className="text-sm font-medium uppercase tracking-widest text-beat-red" data-id="element-383" data-mp="10tjfk99dx5jt">
                    Good to know
                  </h2>
                  <ul className="mt-5 space-y-2 text-base text-white/60" data-id="element-384" data-mp="rrts1eimiqrb">
                    {product.details.map(detail => <li key={detail} data-id="element-385" data-mp="my8rpftk5koy">{detail}</li>)}
                  </ul>
                  <p className="mt-6 text-base leading-relaxed text-white/45" data-id="element-386" data-mp="hz4hqmyt7ixk">
                    {shipping.note}
                  </p>
                </div>
              </section>
            </>}
        </div>
      </main>

      <ShopFooter data-id="element-387" data-mp="41ppzbl32z2f" />
    </div>;
}